import { cn } from "../../lib/utils";

type StatusType =
  | "operational"
  | "warning"
  | "critical"
  | "offline"
  | "active"
  | "inactive"
  | "pending"
  | "completed"
  | "overdue"
  | "in_progress"
  | "open"
  | "closed"
  | "draft";

interface StatusBadgeProps {
  status: string;
  className?: string;
  showDot?: boolean;
}

const STATUS_CONFIG: Record<
  string,
  { color: string; bg: string; dotColor: string; label: string }
> = {
  operational: {
    color: "text-emerald-300",
    bg: "bg-emerald-500/15 border-emerald-500/30",
    dotColor: "bg-emerald-400",
    label: "Operational",
  },
  active: {
    color: "text-emerald-300",
    bg: "bg-emerald-500/15 border-emerald-500/30",
    dotColor: "bg-emerald-400",
    label: "Active",
  },
  completed: {
    color: "text-emerald-300",
    bg: "bg-emerald-500/15 border-emerald-500/30",
    dotColor: "bg-emerald-400",
    label: "Completed",
  },
  closed: {
    color: "text-emerald-300",
    bg: "bg-emerald-500/15 border-emerald-500/30",
    dotColor: "bg-emerald-400",
    label: "Closed",
  },
  warning: {
    color: "text-yellow-300",
    bg: "bg-yellow-500/15 border-yellow-500/30",
    dotColor: "bg-yellow-400",
    label: "Warning",
  },
  pending: {
    color: "text-yellow-300",
    bg: "bg-yellow-500/15 border-yellow-500/30",
    dotColor: "bg-yellow-400",
    label: "Pending",
  },
  in_progress: {
    color: "text-blue-300",
    bg: "bg-blue-500/15 border-blue-500/30",
    dotColor: "bg-blue-400",
    label: "In Progress",
  },
  open: {
    color: "text-blue-300",
    bg: "bg-blue-500/15 border-blue-500/30",
    dotColor: "bg-blue-400",
    label: "Open",
  },
  draft: {
    color: "text-surface-300",
    bg: "bg-surface-500/15 border-surface-500/30",
    dotColor: "bg-surface-400",
    label: "Draft",
  },
  critical: {
    color: "text-red-300",
    bg: "bg-red-500/15 border-red-500/30",
    dotColor: "bg-red-400",
    label: "Critical",
  },
  overdue: {
    color: "text-red-300",
    bg: "bg-red-500/15 border-red-500/30",
    dotColor: "bg-red-400",
    label: "Overdue",
  },
  offline: {
    color: "text-surface-400",
    bg: "bg-surface-600/15 border-surface-600/30",
    dotColor: "bg-surface-500",
    label: "Offline",
  },
  inactive: {
    color: "text-surface-400",
    bg: "bg-surface-600/15 border-surface-600/30",
    dotColor: "bg-surface-500",
    label: "Inactive",
  },
};

export function StatusBadge({
  status,
  className,
  showDot = true,
}: StatusBadgeProps) {
  const normalized = status.toLowerCase().replace(/[\s-]/g, "_") as StatusType;
  const config = STATUS_CONFIG[normalized] || {
    color: "text-surface-300",
    bg: "bg-surface-700/50 border-surface-600/30",
    dotColor: "bg-surface-400",
    label: status,
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        config.bg,
        config.color,
        className
      )}
    >
      {showDot && (
        <span className="relative flex h-1.5 w-1.5">
          {(normalized === "active" || normalized === "operational" || normalized === "in_progress") && (
            <span
              className={cn(
                "absolute inset-0 rounded-full animate-ping opacity-50",
                config.dotColor
              )}
            />
          )}
          <span
            className={cn("relative h-1.5 w-1.5 rounded-full", config.dotColor)}
          />
        </span>
      )}
      {config.label}
    </span>
  );
}
